import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import {
	confirm,
	type OverlayBeforeCloseDetail,
} from "@mhmo91/schmancy/overlay";
import type { Workspace } from "@momo/types";
import { html, nothing, type PropertyValues, type TemplateResult } from "lit";
import { property, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { fromEvent, takeUntil, tap } from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
import {
	INTEGRATION_TONE_CLASS,
	type IntegrationStatusLine,
} from "../status.state.ts";
import {
	type MetaField,
	type MetaSaveIntent,
	type MetaTextField,
	malformedMetaFields,
} from "./meta.state.ts";

type MetaDraft = Readonly<Record<MetaTextField, string>>;
const EMPTY_DRAFT: MetaDraft = {
	accessToken: "",
	appSecret: "",
	pixelId: "",
	appId: "",
	adAccountId: "",
	pageId: "",
	instagramAccountId: "",
};
const ADVERTISER_FIELDS: readonly MetaTextField[] = [
	"appId",
	"adAccountId",
	"pageId",
	"instagramAccountId",
];
const SETUP = [
	{
		id: "app",
		url: "https://developers.facebook.com/docs/development/create-an-app/",
	},
	{ id: "portfolio", url: "https://business.facebook.com/latest/settings" },
	{ id: "pixel", url: "https://business.facebook.com/events_manager2" },
	{
		id: "token",
		url: "https://business.facebook.com/latest/settings/system_users",
	},
] as const;

@schmancyElement("meta-credentials-form")
export class MetaCredentialsForm extends LocalizeMixin(SchmancyElement) {
	@property({ attribute: false }) status: IntegrationStatusLine | null = null;
	@property({ type: Boolean }) saving = false;
	@property({ attribute: false }) lastSaveOk: boolean | null = null;
	@property({ type: Boolean }) hasStoredCredential = false;
	@property({ attribute: false })
	storedAdvertiser: Workspace.MetaAdvertiser | null = null;
	@property({ attribute: false }) storedPixelId: string | null = null;
	@state() private draft: MetaDraft = EMPTY_DRAFT;
	@state() private step: 1 | 2 | 3 = 1;
	@state() private openSetup = 1;
	@state() private replacing: readonly MetaTextField[] = [];
	@state() private runsAds = false;
	@state() private touched: readonly MetaField[] = [];
	@state() private revealed = false;
	@state() private attempted = false;
	@state() private admitted = false;
	@state() private confirming = false;
	@state() private firstAdAccountSave = false;
	@state() private savedWithAds = false;
	private seeded = false;
	private acceptedAdAccountId: string | null = null;
	private submittedAdAccountId: string | null = null;
	private initialDraft: MetaDraft = EMPTY_DRAFT;
	private initialRunsAds = false;

	override connectedCallback(): void {
		super.connectedCallback();
		const root = this.getRootNode();
		if (!(root instanceof ShadowRoot)) return;
		fromEvent<CustomEvent<OverlayBeforeCloseDetail>>(root.host, "beforeclose")
			.pipe(
				tap((event) => {
					if (
						!this.dirty() &&
						!this.saving &&
						!this.admitted &&
						!this.confirming
					)
						return;
					event.preventDefault();
					if (event.detail.reason === "structured") void this.close();
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe();
	}

	protected override willUpdate(changed: PropertyValues<this>): void {
		super.willUpdate(changed);
		if (changed.has("saving") && !this.saving) this.admitted = false;
		if (
			this.attempted &&
			!this.saving &&
			this.lastSaveOk === true &&
			(changed.has("saving") || changed.has("lastSaveOk"))
		) {
			this.step = 3;
			this.acceptedAdAccountId = this.submittedAdAccountId;
			this.admitted = false;
			this.draft = { ...this.draft, accessToken: "", appSecret: "" };
			this.replacing = [];
			this.initialDraft = this.draft;
			this.initialRunsAds = this.runsAds;
		}
		if (this.seeded) return;
		this.seeded = true;
		this.runsAds = this.storedAdvertiser?.adAccountId != null;
		this.initialRunsAds = this.runsAds;
		this.step = this.hasStoredCredential ? 2 : 1;
		this.draft = {
			...EMPTY_DRAFT,
			pixelId: this.storedPixelId ?? "",
			appId: this.storedAdvertiser?.appId ?? "",
			adAccountId: this.storedAdvertiser?.adAccountId ?? "",
			pageId: this.storedAdvertiser?.pageId ?? "",
			instagramAccountId: this.storedAdvertiser?.instagramAccountId ?? "",
		};
		this.initialDraft = this.draft;
	}

	private dirty(): boolean {
		return (
			this.runsAds !== this.initialRunsAds ||
			(
				[
					"accessToken",
					"appSecret",
					"pixelId",
					"appId",
					"adAccountId",
					"pageId",
					"instagramAccountId",
				] as const
			).some((key) => this.draft[key] !== this.initialDraft[key])
		);
	}

	private async close(): Promise<void> {
		if (this.saving || this.admitted || this.confirming) return;
		if (this.dirty()) {
			this.confirming = true;
			const discard = await confirm({
				title: this.t("admin.integrations.metaAds.form.wizard.discardTitle"),
				confirmText: this.t(
					"admin.integrations.metaAds.form.wizard.discardButton",
				),
				cancelText: this.t(
					"admin.integrations.metaAds.form.wizard.keepEditing",
				),
				variant: "danger",
			});
			this.confirming = false;
			if (!discard || !this.isConnected) return;
		}
		this.dispatchEvent(
			new CustomEvent("close", { bubbles: true, composed: true }),
		);
	}

	private field(key: MetaTextField): TemplateResult {
		const credential = key === "accessToken" || key === "appSecret";
		if (credential && this.hasStoredCredential && !this.replacing.includes(key))
			return html`
   <schmancy-surface type="containerLow" class="flex items-center justify-between gap-3 rounded-xl px-4 py-3">
    <div class="flex flex-col gap-1"><schmancy-typography type="body" token="md">${this.t(`admin.integrations.metaAds.form.${key}Label`)}</schmancy-typography><schmancy-typography type="body" token="sm" class="text-surface-onVariant">${this.t("admin.integrations.metaAds.form.wizard.stored")}</schmancy-typography></div>
    <schmancy-button variant="text" ?disabled=${this.saving || this.admitted || this.confirming} @click=${() => {
			this.replacing = [...this.replacing, key];
		}}>${this.t("admin.integrations.metaAds.form.wizard.replace")}</schmancy-button>
   </schmancy-surface>`;
		const invalid = malformedMetaFields({
			secrets: credential && this.draft[key] ? { [key]: this.draft[key] } : {},
			...(!credential && this.draft[key] ? { [key]: this.draft[key] } : {}),
		})[key];
		const failing = invalid && (this.revealed || this.touched.includes(key));
		return html`<schmancy-input name=${key} type=${credential ? "password" : "text"} inputmode=${credential || key === "adAccountId" ? "text" : "numeric"} autocomplete="off"
   ?readonly=${this.saving || this.admitted || this.confirming} ?required=${credential ? !this.hasStoredCredential : key === "pixelId" ? this.storedPixelId == null : (key === "adAccountId" || key === "pageId") && (this.runsAds || this.storedAdvertiser?.adAccountId != null)}
   label=${this.t(`admin.integrations.metaAds.form.${key}Label`)} .value=${this.draft[key]} ?error=${failing}
   hint=${failing ? this.t(credential ? "admin.integrations.metaAds.form.credentialPasteError" : key === "adAccountId" ? "admin.integrations.metaAds.form.adAccountIdFormatError" : key === "pixelId" ? "admin.integrations.metaAds.form.pixelIdFormatError" : "admin.integrations.metaAds.form.numericIdFormatError") : this.t(`admin.integrations.metaAds.form.${key}Hint`)}
   @input=${(e: CustomEvent<{ value: string }>) => {
			if (
				this.saving ||
				this.admitted ||
				this.confirming ||
				e.detail.value === this.draft[key]
			)
				return;
			this.draft = { ...this.draft, [key]: e.detail.value };
		}}
   @blur=${() => {
			if (!this.touched.includes(key)) this.touched = [...this.touched, key];
		}}
  ></schmancy-input>`;
	}

	private async save(intent: MetaSaveIntent): Promise<void> {
		if (this.saving || this.admitted || this.confirming) return;
		this.revealed = true;
		if (Object.values(malformedMetaFields(intent)).some(Boolean)) return;
		if (
			(this.runsAds || this.storedAdvertiser?.adAccountId != null) &&
			(!(intent.adAccountId || this.storedAdvertiser?.adAccountId) ||
				!(intent.pageId || this.storedAdvertiser?.pageId))
		)
			return;
		const firstAdAccount =
			intent.adAccountId != null &&
			this.storedAdvertiser?.adAccountId == null &&
			this.acceptedAdAccountId == null;
		if (firstAdAccount) {
			this.confirming = true;
			const ok = await confirm({
				title: this.t("admin.integrations.metaAds.form.wizard.confirmTitle"),
				message: this.t(
					"admin.integrations.metaAds.form.wizard.confirmMessage",
				),
				confirmText: this.t(
					"admin.integrations.metaAds.form.wizard.confirmButton",
				),
				cancelText: this.t("admin.common.cancel"),
				variant: "danger",
			});
			this.confirming = false;
			if (!ok || !this.isConnected) return;
		}
		this.firstAdAccountSave = firstAdAccount;
		this.submittedAdAccountId =
			intent.adAccountId ??
			this.storedAdvertiser?.adAccountId ??
			this.acceptedAdAccountId;
		this.savedWithAds =
			intent.adAccountId != null || this.storedAdvertiser?.adAccountId != null;
		this.attempted = true;
		this.admitted = true;
		this.dispatchEvent(
			new CustomEvent<MetaSaveIntent>("save", {
				detail: intent,
				bubbles: true,
				composed: true,
			}),
		);
	}

	override render(): TemplateResult {
		const locked = this.storedAdvertiser?.adAccountId != null;
		const advertiser = locked || this.runsAds;
		const intent: MetaSaveIntent = {
			secrets: {
				...(this.draft.accessToken && { accessToken: this.draft.accessToken }),
				...(this.draft.appSecret && { appSecret: this.draft.appSecret }),
			},
			...(this.draft.pixelId && { pixelId: this.draft.pixelId }),
			...(advertiser && this.draft.appId && { appId: this.draft.appId }),
			...(advertiser &&
				this.draft.adAccountId && { adAccountId: this.draft.adAccountId }),
			...(advertiser && this.draft.pageId && { pageId: this.draft.pageId }),
			...(advertiser &&
				this.draft.instagramAccountId && {
					instagramAccountId: this.draft.instagramAccountId,
				}),
		};
		const complete =
			(this.hasStoredCredential ||
				(!!intent.secrets.accessToken && !!intent.secrets.appSecret)) &&
			(this.storedPixelId != null || !!intent.pixelId) &&
			(!advertiser ||
				!!(
					(intent.adAccountId || this.storedAdvertiser?.adAccountId) &&
					(intent.pageId || this.storedAdvertiser?.pageId)
				));
		const busy = this.saving || this.admitted;
		return html`<div class="flex max-h-[85dvh] w-[min(28rem,92vw)] max-w-full flex-col">
   <header class="flex items-center justify-between gap-4 px-5 pt-4 pb-3"><schmancy-typography type="headline" token="md">${this.t("admin.integrations.metaAds.form.wizard.title")}</schmancy-typography><schmancy-button data-overlay-close variant="text" ?disabled=${busy || this.confirming} aria-label=${this.t("admin.integrations.metaAds.form.closeAriaLabel")} @click=${() => this.close()}>${this.t("admin.common.close")}</schmancy-button></header>
   <main ?inert=${busy || this.confirming} ${overflowWithin()} class="flex min-h-0 flex-1 flex-col gap-5 px-5 pb-5">
    <schmancy-steps .value=${this.step} @navigate=${(
			e: CustomEvent<{ value: number }>,
		) => {
			if (busy || this.confirming) return;
			if (
				e.detail.value === 1 ||
				e.detail.value === 2 ||
				(e.detail.value === 3 && this.attempted && this.lastSaveOk === true)
			)
				this.step = e.detail.value;
		}}>
     <schmancy-step label=${this.t("admin.integrations.metaAds.form.wizard.stepSetup")}><div class="flex flex-col gap-4">
      <schmancy-typography type="body" token="sm">${this.t("admin.integrations.metaAds.form.wizard.prerequisites")}</schmancy-typography>
      <schmancy-button variant="text" href="https://business.facebook.com/latest/settings" target="_blank" rel="noopener">${this.t("admin.integrations.metaAds.form.wizard.businessSettings")}<schmancy-icon slot="suffix">open_in_new</schmancy-icon></schmancy-button>
      ${repeat(
				SETUP,
				(s) => s.id,
				(setup, index) =>
					html`<schmancy-details type="containerLow" ?open=${this.openSetup === index + 1} @toggle=${(
						e: CustomEvent<{ open: boolean }>,
					) => {
						this.openSetup = e.detail.open ? index + 1 : 0;
					}}><div slot="summary" class="flex items-baseline gap-3"><schmancy-typography type="label" token="md" class="text-surface-onVariant">${index + 1}</schmancy-typography><schmancy-typography type="title" token="sm">${this.t(`admin.integrations.metaAds.form.wizard.${setup.id}Title`)}</schmancy-typography></div><div class="flex flex-col gap-3"><schmancy-typography type="body" token="sm">${this.t(`admin.integrations.metaAds.form.wizard.${setup.id}Body`)}</schmancy-typography>${setup.id === "token" ? html`<schmancy-typography type="body" token="sm" class="text-warning-default">${this.t("admin.integrations.metaAds.form.wizard.wrongToken")}</schmancy-typography>` : nothing}<schmancy-button variant="text" href=${setup.url} target="_blank" rel="noopener">${this.t(`admin.integrations.metaAds.form.wizard.${setup.id}Link`)}<schmancy-icon slot="suffix">open_in_new</schmancy-icon></schmancy-button></div></schmancy-details>`,
			)}
     </div></schmancy-step>
     <schmancy-step label=${this.t("admin.integrations.metaAds.form.wizard.stepValues")}><div class="flex flex-col gap-5">
      ${when(this.status?.tone === "error" || this.status?.tone === "warning", () => (this.status ? html`<schmancy-typography type="body" token="sm" role="alert" class=${INTEGRATION_TONE_CLASS[this.status.tone]}>${this.t(this.status.message, this.status.params)}</schmancy-typography>` : nothing))}
      <section class="flex flex-col gap-3"><schmancy-typography type="title" token="sm">${this.t("admin.integrations.metaAds.form.credentialsHeading")}</schmancy-typography>${this.field("accessToken")}${this.field("appSecret")}</section>
      <section class="flex flex-col gap-3"><schmancy-typography type="title" token="sm">${this.t("admin.integrations.metaAds.form.measurementHeading")}</schmancy-typography>${this.field("pixelId")}</section>
      <section class="flex flex-col gap-3">
       ${
					locked
						? html`<schmancy-typography type="body" token="sm"><schmancy-icon size="sm">lock</schmancy-icon> ${this.t("admin.integrations.metaAds.form.wizard.lockedNote")}</schmancy-typography>`
						: html`<schmancy-radio-group label=${this.t("admin.integrations.metaAds.form.wizard.scopeLabel")} .value=${this.runsAds ? "ads" : "measurement"} @change=${(
								e: CustomEvent<{ value: string }>,
							) => {
								if (!busy && !this.confirming)
									this.runsAds = e.detail.value === "ads";
							}}><schmancy-radio-button value="measurement" label=${this.t("admin.integrations.metaAds.form.wizard.measurementLabel")}>${this.t("admin.integrations.metaAds.form.wizard.measurementLabel")}</schmancy-radio-button><schmancy-radio-button value="ads" label=${this.t("admin.integrations.metaAds.form.wizard.adsLabel")}>${this.t("admin.integrations.metaAds.form.wizard.adsLabel")}</schmancy-radio-button></schmancy-radio-group>`
				}
       ${when(
					advertiser,
					() =>
						html`${repeat(
							ADVERTISER_FIELDS,
							(k) => k,
							(k) => this.field(k),
						)}`,
				)}
      </section>
     </div></schmancy-step>
     <schmancy-step label=${this.t("admin.integrations.metaAds.form.wizard.stepDone")}><div class="flex flex-col gap-4"><schmancy-icon class="self-start text-4xl text-primary-default">check_circle</schmancy-icon><schmancy-typography type="title" token="lg">${this.t(this.firstAdAccountSave ? "admin.integrations.metaAds.form.wizard.accountSavedTitle" : "admin.integrations.metaAds.form.wizard.doneTitle")}</schmancy-typography><schmancy-typography type="body" token="md">${this.t(this.firstAdAccountSave ? "admin.integrations.metaAds.form.wizard.firstAccountNote" : this.savedWithAds ? "admin.integrations.metaAds.form.wizard.doneAds" : "admin.integrations.metaAds.form.wizard.doneMeasurement")}</schmancy-typography></div></schmancy-step>
    </schmancy-steps>
   </main>
   <schmancy-surface part="footer" opacity="translucent" elevated class="flex shrink-0 flex-wrap items-center justify-between gap-3 px-5 py-4">
    ${
			this.step === 2
				? html`<schmancy-button variant="text" ?disabled=${busy || this.confirming} @click=${() => {
						this.step = 1;
					}}>${this.t("admin.integrations.metaAds.form.wizard.setupAgain")}</schmancy-button>`
				: nothing
		}
    <schmancy-button class="ml-auto" variant="filled" .busy=${busy} ?disabled=${this.confirming || (this.step === 2 && !complete)} @click=${() => {
			if (this.step === 1) this.step = 2;
			else if (this.step === 3) {
				if (this.firstAdAccountSave) this.step = 2;
				else void this.close();
			} else void this.save(intent);
		}}>${this.t(this.step === 1 ? "admin.integrations.metaAds.form.wizard.continueButton" : this.step === 3 ? (this.firstAdAccountSave ? "admin.integrations.metaAds.form.wizard.continueVerification" : "admin.integrations.metaAds.form.closeButton") : "admin.integrations.metaAds.form.saveButton")}</schmancy-button>
   </schmancy-surface>
  </div>`;
	}
}
declare global {
	interface HTMLElementTagNameMap {
		"meta-credentials-form": MetaCredentialsForm;
	}
}
